import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import NurseDashboard from './pages/NurseDashboard';
import DoctorDashboard from './pages/DoctorDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import CreatePage from './pages/CreatePage';
import PageDetail from './pages/PageDetail';
import PatientHistory from './pages/PatientHistory';

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/nurse" 
            element={
              <ProtectedRoute allowedRoles={['nurse']}>
                <NurseDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/doctor" 
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <DoctorDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/manager" 
            element={
              <ProtectedRoute allowedRoles={['manager']}>
                <ManagerDashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/create-page" 
            element={
              <ProtectedRoute allowedRoles={['nurse']}>
                <CreatePage />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/page/:id" 
            element={
              <ProtectedRoute>
                <PageDetail />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/patient-history/:pageId" 
            element={
              <ProtectedRoute allowedRoles={['doctor', 'manager']}>
                <PatientHistory />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

// Router component to redirect based on user role
const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (user.role === 'nurse') {
    return <Navigate to="/nurse" replace />;
  } else if (user.role === 'doctor') {
    return <Navigate to="/doctor" replace />;
  } else if (user.role === 'manager') {
    return <Navigate to="/manager" replace />;
  }
  
  return <Navigate to="/login" replace />;
};

export default App;
